import React, { useState } from "react";
import { DownloadSimple, X, CheckCircle, Warning } from "@phosphor-icons/react";
import { usePackJobs } from "@/components/social/usePackJobs";

/**
 * Tray flottante in basso a destra: mostra job pacchetto in lavorazione e
 * job pronti da scaricare. Visibile su tutte le pagine (montato in App.js)
 * così l'agente può scatenare un pack, navigare via, fare altro, e
 * scaricare quando arriva la notifica.
 *
 * Stati visuali:
 *   • running  → spinner + tempo trascorso
 *   • done     → check verde + bottone "Scarica"
 *   • failed   → triangolo + messaggio errore
 *   • expired  → grigio + auto-dismiss dopo qualche secondo
 *
 * Si nasconde automaticamente quando non ci sono job.
 */
export default function PackJobsTray() {
  const { jobs, remove, download } = usePackJobs();
  const [collapsed, setCollapsed] = useState(false);

  if (!jobs || jobs.length === 0) return null;

  const visible = jobs.filter((j) => j.status !== "expired");
  if (visible.length === 0) return null;

  const running = visible.filter((j) => j.status === "queued" || j.status === "running").length;
  const ready = visible.filter((j) => j.status === "done").length;
  const failed = visible.filter((j) => j.status === "failed").length;

  return (
    <div
      className="fixed bottom-4 right-4 z-[60] w-[320px] max-w-[calc(100vw-2rem)] rounded-lg border border-[#1C1C1A]/15 bg-white/95 backdrop-blur-md shadow-lg shadow-black/10 overflow-hidden"
      data-testid="pack-jobs-tray"
    >
      {/* Header collassabile */}
      <button
        onClick={() => setCollapsed((c) => !c)}
        className="w-full flex items-center justify-between px-3 py-2 bg-[#1C1C1A] text-white text-[12px] font-semibold hover:bg-[#2A2A29] transition-colors"
      >
        <div className="flex items-center gap-2">
          {running > 0 && (
            <span className="inline-block w-2 h-2 rounded-full bg-[#B5C4A6] animate-pulse" />
          )}
          {running > 0 && <span>{running} in lavorazione</span>}
          {ready > 0 && (
            <span className="text-[#B5C4A6]">
              {ready === 1 ? "1 pronto" : `${ready} pronti`}
            </span>
          )}
          {failed > 0 && <span className="text-[#E5A48E]">· {failed} errore</span>}
        </div>
        <span className="text-[10px] text-white/55">{collapsed ? "▾" : "▴"}</span>
      </button>

      {/* Lista job */}
      {!collapsed && (
        <div className="divide-y divide-[#EAE7DF] max-h-[320px] overflow-y-auto">
          {visible.map((job) => (
            <PackJobRow
              key={job.job_id}
              job={job}
              onDownload={() => download(job)}
              onDismiss={() => remove(job.job_id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function PackJobRow({ job, onDownload, onDismiss }) {
  const isRunning = job.status === "queued" || job.status === "running";
  const isDone = job.status === "done";
  const isFailed = job.status === "failed";
  const elapsed = _elapsedSeconds(job.started_at);

  return (
    <div className="px-3 py-2.5 flex items-start gap-2" data-testid={`pack-job-${job.job_id}`}>
      <div className="shrink-0 mt-0.5">
        {isRunning && (
          <span className="inline-block w-3.5 h-3.5 border-2 border-[#7C8B6E] border-t-transparent rounded-full animate-spin" />
        )}
        {isDone && <CheckCircle size={16} weight="fill" className="text-[#7C8B6E]" />}
        {isFailed && <Warning size={16} weight="fill" className="text-[#C4734A]" />}
      </div>

      <div className="flex-1 min-w-0">
        <div className="text-[12px] font-semibold text-[#1C1C1A] truncate">
          {job.property_title || job.property_id}
        </div>
        <div className="text-[10.5px] text-[#7C7B73] mt-0.5">
          {isRunning && (
            <>
              In lavorazione · {elapsed >= 60 ? `${Math.floor(elapsed / 60)}:${String(elapsed % 60).padStart(2, "0")}` : `${elapsed}s`}
            </>
          )}
          {isDone && "Pacchetto pronto · scaricalo qui"}
          {isFailed && "Generazione fallita"}
        </div>
      </div>

      <div className="shrink-0 flex items-center gap-1">
        {isDone && (
          <button
            onClick={onDownload}
            className="px-2 py-1 bg-[#1C1C1A] text-white rounded text-[10.5px] font-semibold flex items-center gap-1 hover:bg-[#2A2A29] transition-colors"
            data-testid={`pack-job-download-${job.job_id}`}
          >
            <DownloadSimple size={12} weight="bold" /> Scarica
          </button>
        )}
        {(isDone || isFailed) && (
          <button
            onClick={onDismiss}
            className="p-1 rounded hover:bg-[#EAE7DF] text-[#7C7B73]"
            aria-label="Chiudi"
            data-testid={`pack-job-dismiss-${job.job_id}`}
          >
            <X size={11} weight="bold" />
          </button>
        )}
      </div>
    </div>
  );
}

function _elapsedSeconds(iso) {
  if (!iso) return 0;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return 0;
  return Math.max(0, Math.floor((Date.now() - t) / 1000));
}
